import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import classnames from 'classnames'
import Dashboard from './screens/Dashboard'
import Detail from './screens/Detail'
import BaseCase from './screens/BaseCase'
import Config from './screens/Config'
import { SaberProvider } from './SaberContext'
import { useAppSelector } from '../../shared/redux/hooks'

// v001 IA (§13): four purpose-built tabs, each owning its own date/property
// scoping. WEB-6567: each screen is addressable by URL (/saber/<screen>) so a
// formed URL deep-links to the same screen and the Back button moves between
// screens. Config tab label is Engine Status; URL path stays /saber/config.
const ALL_SCREENS: ReadonlyArray<{ path: string; label: string; permission?: string }> = [
  { path: 'dashboard', label: 'Dashboard' },
  { path: 'detail', label: 'Shadow Billing' },
  { path: 'base-case', label: 'Base Case Model', permission: 'base-case-model:upload' },
  { path: 'config', label: 'Engine Status' }
]

const DEFAULT_SCREEN = 'dashboard'
const SCREEN_PATHS = new Set<string>(ALL_SCREENS.map((s) => s.path))

// Active screen from the URL: the path segment after /saber, falling back to
// dashboard for the bare /saber route or any unrecognized segment.
const screenFromPath = (pathname: string): string => {
  const seg = pathname.startsWith('/saber/') ? pathname.slice('/saber/'.length).split('/')[0] : ''
  return SCREEN_PATHS.has(seg) ? seg : DEFAULT_SCREEN
}

// Header + tab bar on one row — logo left, screen tabs inline to the right.
const SaberTopBar: React.FC<{
  resolvedActive: string
  hasBaseCaseModel: boolean
  onGoToScreen: (path: string) => void
}> = ({ resolvedActive, hasBaseCaseModel, onGoToScreen }) => (
  <div className="flex h-[50px] items-end gap-x-[38px] overflow-x-auto border-b border-slate-200 dark:border-slate-700">
    <div className="flex shrink-0 items-center gap-2">
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">SABER</h1>
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Beta
      </span>
    </div>
    <nav aria-label="Tabs" className="flex min-w-0 flex-1 gap-x-8 overflow-x-auto">
      {ALL_SCREENS.map((tab) => {
        const disabled = tab.permission != null && !hasBaseCaseModel
        return (
          <button
            key={tab.path}
            type="button"
            onClick={disabled ? undefined : () => onGoToScreen(tab.path)}
            disabled={disabled}
            title={disabled ? 'Sufficient permission required to access the Base Case Model.' : undefined}
            aria-current={resolvedActive === tab.path ? 'page' : undefined}
            className={classnames(
              disabled
                ? 'border-transparent text-slate-400 dark:text-slate-600 opacity-50 cursor-not-allowed'
                : resolvedActive === tab.path
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:text-slate-300',
              'whitespace-nowrap border-b-2 px-1 py-2.5 text-sm font-medium'
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </nav>
  </div>
)

const SaberLayoutInner: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { permissions } = useAppSelector((state) => state.app)
  const hasBaseCaseModel = permissions?.includes('base-case-model:upload') ?? false

  const active = screenFromPath(location.pathname)
  // If the user navigates directly to base-case without the upload permission, fall back to dashboard.
  const resolvedActive = active === 'base-case' && !hasBaseCaseModel ? DEFAULT_SCREEN : active

  // Tab switch: push (so Back returns to the previous screen) and carry the
  // current query (selected property / period) across tabs — this is what makes
  // the period selection persist between Dashboard and Shadow Billing (WEB-6581).
  const goToScreen = (path: string): void => {
    void navigate({ pathname: `/saber/${path}`, search: location.search })
  }

  // Dashboard row -> Detail: a single push that selects the property and sets
  // the shared period to the requested month, preserving the rest of the query.
  const openDetail = (propertyCuid: string, period: string): void => {
    const params = new URLSearchParams(location.search)
    params.set('property', propertyCuid)
    params.set('period', period)
    void navigate({ pathname: '/saber/detail', search: `?${params.toString()}` })
  }

  return (
    // Page padding matches the Invoices/Payments Layout (px-4 sm:px-6 md:px-8 + py-6).
    // WEB-6581: tightened the inter-section gap (space-y-3) to reclaim top space.
    <div className="space-y-3 px-4 py-6 sm:px-6 md:px-8">
      <SaberTopBar resolvedActive={resolvedActive} hasBaseCaseModel={hasBaseCaseModel} onGoToScreen={goToScreen} />
      <div className="w-full min-w-0">
        {resolvedActive === 'dashboard' && <Dashboard onOpenDetail={openDetail} />}
        {resolvedActive === 'detail' && <Detail />}
        {resolvedActive === 'base-case' && hasBaseCaseModel && <BaseCase />}
        {resolvedActive === 'config' && <Config />}
      </div>
    </div>
  )
}

export const SaberLayout: React.FC = () => (
  <SaberProvider>
    <SaberLayoutInner />
  </SaberProvider>
)

export default SaberLayout
